import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgentSessionProvider } from '../components/agent/AgentSessionProvider';
import { AgentChat } from '../components/agent/AgentChat';
import type { BackendMessage } from '../lib/agentProtocol';
import './ProjectCanvas.css';

/**
 * /#/__test/project-canvas-scroll — Playwright probe for the Project page
 * "agent chat scrolls with the project" bug (!-Issues/06-agent-chat-in-project).
 *
 * The bug: the project details and the docked agent rail scrolled as ONE long
 * page — to send a message or see the latest reply the user had to scroll to
 * the very bottom of the project. Root cause lives in the height-constraint
 * chain above the split, NOT in the rail itself:
 *
 *   .pn-app (grid, height:100vh, rows --header-h / 1fr)
 *     .pn-app-main (grid-area:main → 1fr track = DEFINITE height)
 *       .pn-shell-main:has(.pn-canvas-split)     ← height cap rule under test
 *         .pn-route-transition (flex:1 1 auto; min-height:0)
 *           .pn-canvas-split (flex row, height:100%)
 *             .pn-canvas (overflow-y:auto)        ← the project scroller
 *             .pn-canvas-chat-rail → .agent-page → .agent-transcript
 *
 * The base `.pn-shell-main` is `min-height:100%` — a FLOOR — so the split's
 * `height:100%` resolved to auto and everything grew with the content. This
 * probe renders the REAL shell chain with NO hardcoded heights (the same
 * strategy as /__test/editor-scroll and /__test/agent-route-scroll), so it
 * fails on the unfixed CSS and passes with the cap rule in place.
 *
 * Window helpers (`window.__projectCanvasScrollProbe`):
 *   - `agentReady()`   — emit the `agents` frame so an agent can be selected.
 *   - `seedChat(n)`    — emit n agent messages so the transcript overflows.
 *   - `metrics()`      — canvas + transcript scroll metrics, window scroll, and
 *                        the composer's viewport position in one call.
 */

interface ProjectCanvasScrollProbe {
  agentReady: () => void;
  seedChat: (n: number) => void;
  metrics: () => {
    viewportHeight: number;
    windowScrollY: number;
    canvasScrollTop: number;
    canvasScrollHeight: number;
    canvasClientHeight: number;
    transcriptScrollTop: number;
    transcriptScrollHeight: number;
    transcriptClientHeight: number;
    composerBottom: number | null;
    composerOffscreen: boolean;
  };
}

interface ProbeWindow extends Window {
  __projectCanvasScrollProbe?: ProjectCanvasScrollProbe;
}

let _socket: DrivableSocket | null = null;

class DrivableSocket {
  readyState = 1;
  onopen: (() => void) | null = null;
  onmessage: ((ev: MessageEvent) => void) | null = null;
  onclose: (() => void) | null = null;
  onerror: (() => void) | null = null;
  send() {
    /* inert */
  }
  close() {
    /* inert */
  }
  emit(msg: BackendMessage) {
    this.onmessage?.({ data: JSON.stringify(msg) } as MessageEvent);
  }
}

function socketFactory(): WebSocket {
  _socket = new DrivableSocket();
  return _socket as unknown as WebSocket;
}

/** A long project body so the canvas is genuinely taller than any realistic
 * viewport. A trailing sentinel marks the bottom-most reachable content. */
const PARAGRAPHS = Array.from({ length: 80 }, (_, i) =>
  `Paragraph ${i + 1} of the project body. Lorem ipsum dolor sit amet, ` +
  'consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore ' +
  'et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.'
);

function ProbeInner() {
  const ready = useRef(false);

  useEffect(() => {
    if (ready.current) return;
    ready.current = true;

    const probe: ProjectCanvasScrollProbe = {
      agentReady: () => {
        const sock = _socket;
        if (!sock) return;
        sock.onopen?.();
        sock.emit({
          type: 'agents',
          agents: [{ id: 'claude-code', name: 'Claude Code', available: true }]
        });
      },
      seedChat: (n: number) => {
        const sock = _socket;
        if (!sock) return;
        for (let i = 0; i < n; i += 1) {
          sock.emit({
            type: 'delta',
            text: `Seed agent message ${i + 1} — `.repeat(8)
          });
          sock.emit({ type: 'done' });
        }
      },
      metrics: () => {
        const canvas = document.querySelector<HTMLElement>(
          '[data-testid="project-canvas"]'
        );
        const transcript = document.querySelector<HTMLElement>(
          '[data-testid="agent-transcript"]'
        );
        const composer = document.querySelector<HTMLElement>(
          '[data-testid="agent-composer"]'
        );
        const vh = window.innerHeight;
        const composerBottom = composer
          ? Math.round(composer.getBoundingClientRect().bottom)
          : null;
        return {
          viewportHeight: vh,
          windowScrollY: window.scrollY,
          canvasScrollTop: canvas?.scrollTop ?? -1,
          canvasScrollHeight: canvas?.scrollHeight ?? -1,
          canvasClientHeight: canvas?.clientHeight ?? -1,
          transcriptScrollTop: transcript?.scrollTop ?? -1,
          transcriptScrollHeight: transcript?.scrollHeight ?? -1,
          transcriptClientHeight: transcript?.clientHeight ?? -1,
          composerBottom,
          composerOffscreen: composerBottom !== null && composerBottom > vh + 1
        };
      }
    };
    (window as ProbeWindow).__projectCanvasScrollProbe = probe;
  }, []);

  // Mirror the REAL signed-in shell + project-route chain (see header). The
  // split gets NO hardcoded height — its height comes only from the chain,
  // exactly as in production.
  return (
    <div className="pn-app" data-testid="project-canvas-scroll-app">
      <aside className="pn-app-rail" aria-label="Primary navigation" />
      <header className="pn-app-header" />
      <main className="pn-app-main">
        <div className="pn-shell-main" data-testid="shell-main">
          <div className="pn-route-transition">
            <div
              className="pn-canvas-split"
              data-testid="project-canvas-split"
              data-chat-open="true"
            >
              <div className="pn-canvas" data-testid="project-canvas">
                <div className="pn-canvas-inner">
                  <header className="pn-proj-head">
                    <h1 className="pn-proj-title">Project scroll probe</h1>
                  </header>
                  {PARAGRAPHS.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                  <p data-testid="project-bottom-sentinel">
                    SENTINEL-BOTTOM-OF-PROJECT
                  </p>
                </div>
              </div>
              <aside
                className="pn-canvas-chat-rail"
                data-testid="project-canvas-chat-rail"
                data-open="true"
              >
                <AgentChat host={{ kind: 'panel' }} />
              </aside>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export function TestProjectCanvasScroll() {
  // params reserved for future variants; referenced to satisfy lint.
  useSearchParams();
  return (
    <AgentSessionProvider socketFactory={socketFactory}>
      <ProbeInner />
    </AgentSessionProvider>
  );
}
